'use client';
import React from 'react';
import { Text } from '@benzinga/core-ui';
import { SmartLink } from '@benzinga/analytics';
import { BzImage } from '@benzinga/image';
import styled from '@benzinga/themetron';
import { CallToActionButton } from './index';
import type { ProductBasedCTAProps, ProductBasedCTALayout } from './ProductBasedCTA';
import { extractImageSize } from '@benzinga/image';
import { StackedCardHeader } from './StackedCardHeader';
import { Icon } from '@benzinga/core-ui';
import { faChevronsDown, faChevronsUp } from '@fortawesome/pro-regular-svg-icons';

export interface StackedCardsProps {
  groups?: ProductBasedCTAProps[];
  layout?: ProductBasedCTALayout;
  social_proofing?: string;
  heading?: string;
  initialItems?: number;
}

export const StackedCards: React.FC<StackedCardsProps> = ({
  groups,
  heading,
  initialItems,
  layout,
  social_proofing,
}) => {
  const [showAll, setShowAll] = React.useState<boolean>(false);
  const items = groups ?? [];
  const splicedItems =
    initialItems && initialItems !== -1 && initialItems > 0
      ? [...items].splice(0, initialItems ?? items.length)
      : items;

  const handleShowAll = React.useCallback(() => {
    setShowAll(!showAll);
  }, [showAll]);

  const data = showAll ? items : splicedItems;

  return (
    <StackedCardsWrapper className={`p-3 ${layout}`}>
      <StackedCardHeader heading={heading} social_proofing={social_proofing} />
      {data &&
        data.map((campaign, index) => {
          const image = extractImageSize(campaign?.image);
          return (
            <div
              className="call-to-action-container flex flex-col sm:flex-row gap-4 w-full justify-between items-center"
              key={index}
            >
              <div className="sm:w-1/3 w-full promo-image-wrap flex items-center justify-center">
                {image?.url && (
                  <div className="image-wrapper">
                    <BzImage alt={campaign.title} height={75} src={image?.url} width={120} />
                  </div>
                )}
              </div>
              <div className="sm:w-2/3 w-full flex justify-center call-to-action-copy">
                <div>
                  <div className="title">{campaign.title}</div>
                  {campaign?.description && (
                    <Text
                      className="call-to-action-description"
                      dangerouslySetInnerHTML={{ __html: campaign?.description }}
                      lines={5}
                    />
                  )}
                  <SmartLink href={campaign?.url}>
                    <CallToActionButton text={campaign?.button_text} variant="flat-blue" />
                  </SmartLink>
                </div>
              </div>
            </div>
          );
        })}
      {initialItems && initialItems != -1 && items.length > initialItems && (
        <div className="view-more-wrap text-right mt-2 cursor-pointer" onClick={handleShowAll}>
          <span className="view-more flex items-center justify-center gap-1 text-md underline pt-2">
            {showAll ? (
              <>
                View Less
                <Icon className="icon-wrap" icon={faChevronsUp} />
              </>
            ) : (
              <>
                View More
                <Icon className="icon-wrap" icon={faChevronsDown} />
              </>
            )}
          </span>
        </div>
      )}
    </StackedCardsWrapper>
  );
};

const StackedCardsWrapper = styled.div`
  border: 2px solid #e0e4e9;
  border-radius: 6px;
  .call-to-action-container {
    border-bottom: 2px solid #e0e4e9;
    padding: 2rem 0;
    line-height: 1.4;
    .image-wrapper {
      margin-bottom: 0;
    }
    .title {
      font-size: 1rem;
      font-weight: 600;
      text-align: center;
      margin-bottom: 1rem;
    }
    .call-to-action-description {
      text-align: center;
      margin-bottom: 0.5rem;
    }
    .cta-button {
      width: 300px;
      margin: 0 auto;
    }
    &:last-child {
      border-bottom: none;
    }
  }
  .view-more {
    font-size: 14px;
    line-height: 1;
    .icon-wrap {
      font-size: 10px;
    }
  }
`;
